/**
 * รูปแบบข้อมูลของรอยเขียน + การวาดลงผืนผ้าใบ (D7)
 *
 * ★ รูปแบบนี้ต้องตรงกับ `scratchpads.strokes` ใน DB เป๊ะ ๆ (02-DB-SCHEMA §10.4)
 *     [{ c: สี, w: ความหนา, p: [[x, y, pressure], ...] }]
 *   พิกัดเป็น **สัดส่วน 0–1 ของ canvas** ไม่ใช่ pixel ดิบ
 *   → หมุน iPad หรือเปลี่ยนขนาดจอแล้วเส้นไม่เพี้ยน
 *
 * ⚠️ เก็บเป็นเส้น **ไม่ใช่รูปภาพ** — PNG ใหญ่กว่า 10–50 เท่า undo ไม่ได้
 *   และหมุนจอแล้วเพี้ยน (D7)
 *
 * ⚠️ PDPA — ลายมือเด็กเก็บใน DB ของเราเท่านั้น **ห้ามส่งเข้า AI**
 *   `app/ai/pdpa_guard.py` ฝั่ง backend ปฏิเสธคีย์ `strokes`/`scratchpad` อยู่แล้ว
 */

/** สีปากกา 4 สีตามที่ตกลงใน D7 — ค่าอ่านจาก CSS custom property */
export const PEN_VARS = ['--pen-1', '--pen-2', '--pen-3', '--pen-4'] as const
export type PenIndex = 0 | 1 | 2 | 3

/** ความหนา 2 ระดับ */
export const WIDTHS = [2, 5] as const

/** จุดหนึ่งจุด — [x, y, แรงกด] ทั้งสามค่าอยู่ในช่วง 0–1 */
export type Point = readonly [number, number, number]

export interface Stroke {
  /** ดัชนีสีปากกา (0–3) — เก็บเป็นดัชนีไม่ใช่ค่าสี เพื่อให้เปลี่ยนธีมแล้วสีตามได้ */
  readonly c: PenIndex
  readonly w: number
  readonly p: Point[]
}

/** เพดาน 200 KB ต่อข้อ (`scratchpads_bytes_ck`) — เกินแล้วต้องเตือน ไม่ใช่ตัดเงียบ ๆ */
export const MAX_BYTES = 204_800

/**
 * ปัดพิกัดเหลือทศนิยม 1 ตำแหน่ง (D7)
 *
 * ตาเปล่าไม่เห็นความต่างบนจอ iPad (0.1% ของความกว้าง ≈ 1px)
 * แต่ขนาดข้อมูลลดลงเกินครึ่ง ซึ่งเป็นตัวตัดสินว่าจะชนเพดาน 200 KB เมื่อไหร่
 */
export function roundPoint(x: number, y: number, pressure: number): Point {
  return [Math.round(x * 1000) / 1000, Math.round(y * 1000) / 1000, Math.round(pressure * 10) / 10]
}

export function serialise(strokes: readonly Stroke[]): string {
  return JSON.stringify(strokes)
}

export function byteSize(strokes: readonly Stroke[]): number {
  return new TextEncoder().encode(serialise(strokes)).length
}

export function strokeCount(strokes: readonly Stroke[]): number {
  return strokes.length
}

function cssVar(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || '#000'
}

/**
 * ตั้งขนาดผืนผ้าใบตาม devicePixelRatio
 *
 * ⚠️ ถ้าไม่คูณ dpr เส้นจะเบลอบนจอ Retina — และเด็กจะรู้สึกว่า "ปากกาไม่คม"
 * ทั้งที่เป็นเรื่องความละเอียดของผืนผ้าใบ ไม่ใช่เรื่องการวาด
 */
export function fitCanvas(canvas: HTMLCanvasElement, cssHeight: number): CanvasRenderingContext2D {
  const dpr = Math.max(1, window.devicePixelRatio || 1)
  const width = canvas.clientWidth || 600
  canvas.width = Math.round(width * dpr)
  canvas.height = Math.round(cssHeight * dpr)
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('เบราว์เซอร์นี้ไม่รองรับ canvas 2d')
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  return ctx
}

/** วาดใหม่ทั้งหมด — ★ ใช้เฉพาะตอน undo / resize / เล่นซ้ำ เท่านั้น */
export function paintAll(
  canvas: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D,
  strokes: readonly Stroke[],
): void {
  const w = canvas.clientWidth
  const h = canvas.clientHeight
  ctx.clearRect(0, 0, w, h)
  for (const stroke of strokes) {
    ctx.strokeStyle = cssVar(PEN_VARS[stroke.c] ?? PEN_VARS[0])
    ctx.lineWidth = stroke.w
    ctx.beginPath()
    stroke.p.forEach(([x, y], i) => {
      const px = x * w
      const py = y * h
      if (i === 0) ctx.moveTo(px, py)
      else ctx.lineTo(px, py)
    })
    // จุดเดียว (แตะแล้วปล่อย) ต้องเห็นเป็นจุด ไม่ใช่หายไป
    const only = stroke.p.length === 1 ? stroke.p[0] : undefined
    if (only) ctx.lineTo(only[0] * w + 0.1, only[1] * h + 0.1)
    ctx.stroke()
  }
}

/**
 * ระยะจากจุดถึงเส้นตรง (segment) — ใช้ทำ hit test ของยางลบ
 *
 * ⚠️ mockup วัดระยะถึง "จุดที่บันทึกไว้" อย่างเดียว ซึ่งพลาดเมื่อลากเร็ว
 * เพราะจุดห่างกันมาก การแตะกลางช่วงระหว่างสองจุดจะไม่โดนอะไรเลย
 * ของจริงต้องวัดถึง **ส่วนของเส้น** ระหว่างจุด ไม่ใช่ถึงตัวจุด
 */
function distanceToSegment(
  px: number,
  py: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
): number {
  const dx = bx - ax
  const dy = by - ay
  const lenSq = dx * dx + dy * dy
  if (lenSq === 0) return Math.hypot(px - ax, py - ay)
  let t = ((px - ax) * dx + (py - ay) * dy) / lenSq
  t = Math.max(0, Math.min(1, t))
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy))
}

/** หาเส้นที่อยู่ใกล้จุดที่แตะที่สุด — คืน -1 ถ้าไม่มีเส้นไหนอยู่ในระยะ */
export function hitTest(
  strokes: readonly Stroke[],
  x: number,
  y: number,
  tolerance = 0.02,
): number {
  let best = -1
  let bestDistance = tolerance
  strokes.forEach((stroke, index) => {
    const pts = stroke.p
    const first = pts[0]
    if (!first) return
    if (pts.length === 1) {
      const d = Math.hypot(first[0] - x, first[1] - y)
      if (d < bestDistance) {
        bestDistance = d
        best = index
      }
      return
    }
    for (let i = 1; i < pts.length; i += 1) {
      const a = pts[i - 1]
      const b = pts[i]
      if (!a || !b) continue
      const d = distanceToSegment(x, y, a[0], a[1], b[0], b[1])
      if (d < bestDistance) {
        bestDistance = d
        best = index
      }
    }
  })
  return best
}
